import { Box, Button, Chip, ChipGroup, Field, HStack, Icon, Pressable, Text, VStack, cx } from '@aj/ui';
import { useEffect, useRef, useState } from 'react';

import { useAppData } from '../data/AppDataProvider.js';
import { useRecentTopics, useRecentTracks } from '../data/hooks.js';
import { useFocusSession } from '../hooks/useFocusSession.js';
import { useI18n, useT } from '../providers/I18nProvider.js';

/**
 * ফোকাস — the protected block.
 *
 * Full screen with the tab bar gone, which is the strongest statement §46 can
 * make: for the length of this session there is nothing in the app to browse.
 * Focus is an action, not a destination (§47) — a Focus *tab* would be a place
 * students go to configure a timer instead of working.
 *
 * Three prompts, each earning its keystroke (§48 #7):
 *
 *  • **On start, what exactly.** A block with no stated aim becomes
 *    tab-switching with a timer running.
 *  • **On finish, what you remember.** Retrieval practice performed rather
 *    than recommended — d = 0.54, and it costs one line.
 *  • **On finish, where to start next time.** A written re-entry point is what
 *    makes tomorrow's session begin in ten seconds.
 *
 * All three are skippable. The minutes are the record; the prompts must never
 * stand between a student and credit for work they actually did.
 */

type Phase = 'setup' | 'running' | 'wrap';

export type FocusProps = { onClose: () => void };

export function Focus({ onClose }: FocusProps) {
  const { features } = useAppData();
  const tracks = useRecentTracks();
  const session = useFocusSession();

  // A session restored from storage lands straight in `running` — a student
  // whose phone killed the tab must come back to a running timer, not a form.
  const [phase, setPhase] = useState<Phase>(session.timer ? 'running' : 'setup');
  const [minutes, setMinutes] = useState(0);

  return phase === 'wrap' ? (
    <Wrap
      minutes={minutes}
      onDone={(wrap) => {
        void session.finish(wrap);
        onClose();
      }}
    />
  ) : phase === 'running' && session.timer ? (
    <Running
      session={session}
      onFinish={() => {
        setMinutes(session.elapsed);
        // Under a minute there is nothing honest to record, so there is
        // nothing to reflect on either.
        if (session.elapsed < 1) {
          void session.abandon();
          onClose();
        } else {
          setPhase('wrap');
        }
      }}
      onAbandon={() => {
        void session.abandon();
        onClose();
      }}
    />
  ) : (
    <Setup
      tracks={tracks}
      presets={features.timer.presets}
      onCancel={onClose}
      onStart={(input) => {
        session.start(input);
        setPhase('running');
      }}
    />
  );

}

const shell =
  'fixed inset-0 z-50 flex flex-col overflow-y-auto bg-canvas px-6 py-8';

function Setup({
  tracks,
  presets,
  onStart,
  onCancel,
}: {
  tracks: string[];
  presets: number[];
  onStart: (input: { track: string; planMinutes: number; intention?: string }) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const i18n = useI18n();
  const [track, setTrack] = useState(tracks[0] ?? '');
  const [plan, setPlan] = useState(presets[1] ?? presets[0] ?? 25);
  const [intention, setIntention] = useState('');
  // Reuse has to be a tap, or the review scheduler ends up with three
  // schedules for one chapter.
  const topics = useRecentTopics(track);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => field.current?.focus(), []);

  return (
    <Box className={shell}>
      <VStack gap={6} className="mx-auto w-full max-w-md">
        <HStack justify="between" align="center">
          <Text as="h1" variant="title" tone="ink">
            {t('focus.title')}
          </Text>
          <Pressable
            onClick={onCancel}
            aria-label={t('now.close')}
            className="grid size-10 place-items-center rounded-lg text-subtle hover:bg-sunken"
          >
            <Icon name="x" size={18} />
          </Pressable>
        </HStack>

        {tracks.length > 0 && (
          <VStack gap={2}>
            <Text variant="overline" tone="subtle">
              {t('focus.setup')}
            </Text>
            <HStack gap={2} className="flex-wrap">
              {tracks.map((name) => (
                <Pressable
                  key={name}
                  aria-pressed={track === name}
                  onClick={() => setTrack(name)}
                  className="rounded-full"
                >
                  <Chip tone={track === name ? 'accent' : 'neutral'}>{name}</Chip>
                </Pressable>
              ))}
            </HStack>
          </VStack>
        )}

        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('focus.intention')}
          </Text>
          <Field
            inputRef={field}
            value={intention}
            onChange={setIntention}
            label={t('focus.intention')}
            placeholder={t('focus.intentionPlaceholder')}
            className="w-full"
          />
          {topics.length > 0 && (
            <ChipGroup
              label={t('focus.intention')}
              options={topics.map((label) => ({ value: label, label }))}
              value={intention}
              onSelect={setIntention}
              clearable
            />
          )}
          <Text variant="caption" tone="subtle">
            {t('focus.intentionHint')}
          </Text>
        </VStack>

        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('focus.howLong')}
          </Text>
          <HStack gap={2} className="flex-wrap">
            {presets.map((preset) => (
              <Pressable
                key={preset}
                aria-pressed={plan === preset}
                onClick={() => setPlan(preset)}
                className={cx(
                  'min-h-11 rounded-lg border px-3 tabular-nums',
                  plan === preset
                    ? 'border-transparent bg-accent text-on-accent'
                    : 'border-line bg-sunken text-ink hover:bg-raised',
                )}
              >
                <Text variant="body" numeric className="text-current">
                  {i18n.formatDuration(preset)}
                </Text>
              </Pressable>
            ))}
          </HStack>
        </VStack>

        <Button
          icon="bolt"
          fullWidth
          disabled={track.trim().length === 0}
          onClick={() =>
            onStart({
              track: track.trim(),
              planMinutes: plan,
              ...(intention.trim() ? { intention: intention.trim() } : {}),
            })
          }
        >
          {t('focus.begin')}
        </Button>
      </VStack>
    </Box>
  );
}

function Running({
  session,
  onFinish,
  onAbandon,
}: {
  session: ReturnType<typeof useFocusSession>;
  onFinish: () => void;
  onAbandon: () => void;
}) {
  const t = useT();
  const i18n = useI18n();
  const [confirming, setConfirming] = useState(false);
  const { timer, running, remainingSeconds, elapsed } = session;

  const over = remainingSeconds < 0;
  const shown = Math.abs(remainingSeconds);
  const clock = `${Math.floor(shown / 60)}:${String(shown % 60).padStart(2, '0')}`;

  return (
    <Box className={cx(shell, 'items-center justify-center')}>
      <VStack gap={6} align="center" className="w-full max-w-md text-center">
        {timer?.intention && (
          <Text variant="body" tone="muted">
            {timer.intention}
          </Text>
        )}
        <Text variant="overline" tone="subtle">
          {timer?.track}
        </Text>

        {/* The one number on the screen. Proportional figures rather than
            tabular: at this size tabular digits read loose. */}
        <Text
          variant="display"
          tone={over ? 'flame' : 'ink'}
          className="text-6xl"
          aria-live="polite"
        >
          {over ? `+${clock}` : clock}
        </Text>

        <Text variant="caption" tone="subtle">
          {!running
            ? t('focus.paused')
            : over
              ? t('focus.over')
              : t('focus.elapsed', { minutes: i18n.formatDuration(elapsed) })}
        </Text>

        <VStack gap={3} className="w-full">
          <Button icon="check" fullWidth onClick={onFinish}>
            {t('focus.done')}
          </Button>
          <Button variant="secondary" fullWidth onClick={running ? session.pause : session.resume}>
            {t(running ? 'focus.pause' : 'focus.resume')}
          </Button>

          {/* Abandoning is confirmed because it throws work away, and never
              styled as danger: bailing on a session is an ordinary thing a
              student does, not a transgression. */}
          {confirming ? (
            <VStack gap={2}>
              <Text variant="caption" tone="muted">
                {t('focus.confirmAbandon')}
              </Text>
              <HStack gap={2}>
                <Button variant="secondary" fullWidth onClick={() => setConfirming(false)}>
                  {t('focus.keep')}
                </Button>
                <Button variant="secondary" fullWidth onClick={onAbandon}>
                  {t('focus.give')}
                </Button>
              </HStack>
            </VStack>
          ) : (
            <Pressable
              onClick={() => setConfirming(true)}
              className="self-center rounded-lg px-3 py-2 text-sm text-subtle hover:text-ink"
            >
              {t('focus.give')}
            </Pressable>
          )}
        </VStack>
      </VStack>
    </Box>
  );
}

function Wrap({
  minutes,
  onDone,
}: {
  minutes: number;
  onDone: (wrap: { nextStep?: string; recall?: string }) => void;
}) {
  const t = useT();
  const i18n = useI18n();
  const [recall, setRecall] = useState('');
  const [nextStep, setNextStep] = useState('');
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => field.current?.focus(), []);

  const submit = () =>
    onDone({
      ...(recall.trim() ? { recall: recall.trim() } : {}),
      ...(nextStep.trim() ? { nextStep: nextStep.trim() } : {}),
    });

  return (
    <Box className={shell}>
      <VStack gap={6} className="mx-auto w-full max-w-md">
        <VStack gap={1}>
          <Text as="h1" variant="title" tone="ink">
            {t('focus.wrapTitle')}
          </Text>
          {/* The minutes are already earned. Saying so up front is what makes
              the two questions feel like an offer rather than a toll. */}
          <Text variant="body" tone="accent" numeric>
            {t('focus.recorded', { minutes: i18n.formatDuration(minutes) })}
          </Text>
        </VStack>

        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('focus.recall')}
          </Text>
          <Field
            inputRef={field}
            value={recall}
            onChange={setRecall}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
            label={t('focus.recall')}
            placeholder={t('focus.recallPlaceholder')}
            className="w-full"
          />
          <Text variant="caption" tone="subtle">
            {t('focus.recallHint')}
          </Text>
        </VStack>

        <VStack gap={2}>
          <Text variant="overline" tone="subtle">
            {t('focus.nextStep')}
          </Text>
          <Field
            value={nextStep}
            onChange={setNextStep}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
            label={t('focus.nextStep')}
            placeholder={t('focus.nextStepPlaceholder')}
            className="w-full"
          />
          <Text variant="caption" tone="subtle">
            {t('focus.nextStepHint')}
          </Text>
        </VStack>

        <VStack gap={2}>
          <Button icon="check" fullWidth onClick={submit}>
            {t('focus.save')}
          </Button>
          <Pressable
            onClick={() => onDone({})}
            className="self-center rounded-lg px-3 py-2 text-sm text-subtle hover:text-ink"
          >
            {t('focus.skip')}
          </Pressable>
        </VStack>
      </VStack>
    </Box>
  );
}
